import { CONFIG, goToCheckout } from "../../config";
import { ArrowUpRight, ShieldCheck, Sparkles, CircleCheckBig } from "lucide-react";
import { useI18n } from "../../i18n";

export default function Hero() {
  const { t, currency, fmtCcy } = useI18n();
  const price = fmtCcy(currency.price);
  const mrp = fmtCcy(currency.mrp);
  return (
    <section id="top" className="relative pt-12 sm:pt-16 pb-16 sm:pb-20 overflow-hidden">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[520px] sm:w-[780px] h-[520px] sm:h-[780px] gold-ring rounded-full pointer-events-none" />
      <div className="absolute inset-0 grain opacity-40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
        <div className="lg:col-span-7 rise">
          <div className="flex items-center gap-2 mb-5 sm:mb-6 flex-wrap">
            <span className="chip">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--green)] pulse-dot" />
              {t("hero_badge1")}
            </span>
            <span className="chip">
              <Sparkles className="w-3 h-3 text-[color:var(--gold)]" /> {t("hero_badge2")}
            </span>
            <span className="chip" style={{ color: "#ff9c9c", borderColor: "rgba(255,92,92,.35)" }}>
              {t("hero_badge_copies", { n: CONFIG.COPIES_LEFT })}
            </span>
          </div>

          <h1 className="font-display font-black text-[34px] sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight">
            {t("hero_h1_a")}{" "}
            <span className="text-[color:var(--gold)] glow-gold">{t("hero_h1_b")}</span>{" "}
            <br className="hidden sm:block" />
            {t("hero_h1_c")}
          </h1>

          <p className="mt-5 sm:mt-6 text-[14px] sm:text-base text-[color:var(--ink-dim)] max-w-xl leading-relaxed">
            {t("hero_sub")}
          </p>

          <div className="mt-6 flex items-baseline gap-2 sm:gap-3 flex-wrap">
            <span className="font-display font-black text-[40px] sm:text-5xl text-[color:var(--gold)] glow-gold">{price}</span>
            <span className="text-[color:var(--ink-mute)] text-lg sm:text-xl line-through">{mrp}</span>
            <span className="text-[10px] sm:text-xs font-mono uppercase tracking-widest px-2 py-1 rounded-full"
              style={{ background: "rgba(0,255,136,.12)", color: "var(--green)" }}>
              −{Math.round((1 - currency.price / currency.mrp) * 100)}% {t("hero_save")}
            </span>
          </div>

          <div className="mt-6 flex flex-wrap gap-2 sm:gap-3">
            <button onClick={() => goToCheckout(currency.code, currency.price)} data-testid="hero-cta-buy"
              className="btn-gold inline-flex items-center gap-2 h-12 px-5 sm:px-6 rounded-full text-sm font-display">
              {t("hero_cta_primary")} <ArrowUpRight className="w-4 h-4" />
            </button>
            <a href="#pricing" data-testid="hero-cta-learn"
              className="btn-ghost inline-flex items-center gap-2 h-12 px-5 sm:px-6 rounded-full text-sm font-display">
              {t("pricing_what_get")}
            </a>
          </div>

          <div className="mt-7 grid grid-cols-3 gap-2 sm:gap-3 max-w-lg">
            {[["hero_pill1", "XAUUSD"], ["hero_pill2", "Prop"], ["hero_pill3", "Lifetime"]].map(([k, val]) => (
              <div key={k} className="panel p-2.5 sm:p-3">
                <div className="font-mono text-[9px] sm:text-[10px] uppercase text-[color:var(--ink-mute)] tracking-widest">{t(k)}</div>
                <div className="font-display font-bold text-[13px] sm:text-[15px] mt-1">{val}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 rise" style={{ animationDelay: "120ms" }}>
          <div className="panel p-5 sm:p-6 lg:p-7 relative">
            <div className="absolute -top-12 -right-10 w-52 h-52 rounded-full bg-[color:var(--gold)] opacity-[0.08] blur-3xl" />
            <div className="flex items-center gap-3">
              <img src="https://c.mql5.com/31/1994/dhokiyas-prop-passer-gold-ea-logo-200x200-9344.png"
                alt="Prop Passer Gold EA" className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl ring-1 ring-[#23232a]" loading="lazy" />
              <div>
                <div className="font-display font-bold text-base sm:text-lg">{CONFIG.PRODUCT_NAME}</div>
                <div className="font-mono text-[10px] sm:text-[11px] uppercase tracking-widest text-[color:var(--ink-mute)]">
                  Expert Advisor · v1.23 · MT5
                </div>
              </div>
            </div>

            <div className="divider-grad my-4 sm:my-5" />

            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              {[["hero_card_sym", "XAUUSD"], ["hero_card_plat", "MetaTrader 5"], ["hero_card_strat", t("hero_card_strat_v")], ["hero_card_prot", t("hero_card_prot_v")]].map(([k, v]) => (
                <div key={k} className="border border-[#23232a] rounded-xl p-2.5 sm:p-3">
                  <div className="font-mono text-[9px] sm:text-[10px] uppercase text-[color:var(--ink-mute)] tracking-widest">{t(k)}</div>
                  <div className="font-display font-semibold text-[13px] sm:text-sm mt-1">{v}</div>
                </div>
              ))}
            </div>

            <ul className="mt-4 sm:mt-5 space-y-2">
              {["hero_card_b1", "hero_card_b2", "hero_card_b3", "hero_card_b4"].map((k) => (
                <li key={k} className="flex items-center gap-2 text-[13px] sm:text-sm text-[color:var(--ink-dim)]">
                  <CircleCheckBig className="w-4 h-4 text-[color:var(--green)] shrink-0" /> {t(k)}
                </li>
              ))}
            </ul>

            <button onClick={() => goToCheckout(currency.code, currency.price)} data-testid="hero-card-buy"
              className="mt-5 sm:mt-6 btn-gold w-full h-12 rounded-full inline-flex items-center justify-center gap-2 text-sm font-display">
              {t("hero_card_buy", { price })} <ArrowUpRight className="w-4 h-4" />
            </button>

            <div className="mt-3 sm:mt-4 flex items-center gap-2 text-[11px] sm:text-xs text-[color:var(--ink-mute)]">
              <ShieldCheck className="w-3.5 h-3.5 text-[color:var(--gold)]" />
              {t("hero_card_trust")}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
